import { useEffect, useState, type FormEvent } from 'react';
import { ShieldCheck, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useRecordQC } from '@/hooks/useProduction';
import {
  STATIONS, STATION_LABELS,
  type Station, type QCOutcome,
  type WorkTicket,
} from '@/types/production';

type Props = {
  open: boolean;
  onClose: () => void;
  ticket: WorkTicket;
};

const DEFECT_CATEGORIES = [
  'Finish · surface',
  'Finish · color match',
  'Veneer · grain match',
  'Veneer · defect',
  'Dimensions · out of tolerance',
  'Hardware · missing / damaged',
  'Assembly · alignment',
  'Packaging',
  'Other',
];

export function RecordQCModal({ open, onClose, ticket }: Props) {
  const record = useRecordQC();

  const [outcome, setOutcome] = useState<QCOutcome>('pass');
  const [defectCategory, setDefectCategory] = useState('');
  const [reworkStation, setReworkStation] = useState<Station>('panel_line');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOutcome('pass');
      setDefectCategory('');
      setReworkStation('panel_line');
      setNotes('');
      setError(null);
    }
  }, [open]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (outcome !== 'pass' && !defectCategory) {
      setError('Please select a defect category.');
      return;
    }
    try {
      await record.mutateAsync({
        id: ticket.id,
        outcome,
        defectCategory: outcome === 'pass' ? undefined : defectCategory,
        reworkStation: outcome === 'rework' ? reworkStation : undefined,
        notes,
      });
      toast.success(
        outcome === 'pass' ? 'QC pass recorded' :
        outcome === 'rework' ? `Sent back to ${STATION_LABELS[reworkStation]}` :
        'QC fail recorded',
      );
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'QC recording failed.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker="Quality Control"
      title={`QC check on ${ticket.number}`}
      description="Record the QC outcome. Rework will send the ticket back to the chosen station; fail will block it."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={record.isPending}>Cancel</Button>
          <Button type="submit" form="qc-form" loading={record.isPending} leftIcon={<ShieldCheck size={13} />}>
            Record Check
          </Button>
        </>
      }
    >
      <form id="qc-form" onSubmit={onSubmit} className="space-y-5 py-2">
        {/* Outcome selector — visual tab bar */}
        <div>
          <div className="kicker mb-2">Outcome</div>
          <div className="grid grid-cols-3 gap-1 p-1 bg-bg-subtle border border-border rounded-sm">
            <OutcomeOption
              icon={CheckCircle2}
              label="Pass"
              active={outcome === 'pass'}
              tone="success"
              onClick={() => setOutcome('pass')}
            />
            <OutcomeOption
              icon={AlertCircle}
              label="Rework"
              active={outcome === 'rework'}
              tone="warning"
              onClick={() => setOutcome('rework')}
            />
            <OutcomeOption
              icon={XCircle}
              label="Fail"
              active={outcome === 'fail'}
              tone="danger"
              onClick={() => setOutcome('fail')}
            />
          </div>
        </div>

        {outcome !== 'pass' && (
          <>
            <Select
              label="Defect Category"
              required
              value={defectCategory}
              onChange={(e) => setDefectCategory(e.target.value)}
              options={[
                { value: '', label: '— Select —' },
                ...DEFECT_CATEGORIES.map((c) => ({ value: c, label: c })),
              ]}
            />
            {outcome === 'rework' && (
              <Select
                label="Send back to station"
                value={reworkStation}
                onChange={(e) => setReworkStation(e.target.value as Station)}
                options={STATIONS
                  .filter((s) => s !== 'qc' && s !== 'packaging' && s !== 'shipping')
                  .map((s) => ({ value: s, label: STATION_LABELS[s] }))}
                hint="The ticket will return to this station."
              />
            )}
          </>
        )}

        <Textarea
          label="Notes"
          rows={3}
          placeholder={
            outcome === 'pass'
              ? 'Optional observations'
              : 'Describe the defect in detail'
          }
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}

function OutcomeOption({
  icon: Icon, label, active, tone, onClick,
}: {
  icon: typeof CheckCircle2;
  label: string;
  active: boolean;
  tone: 'success' | 'warning' | 'danger';
  onClick: () => void;
}) {
  const activeClass = {
    success: 'bg-success/15 text-success border-success/40',
    warning: 'bg-warning/15 text-warning border-warning/40',
    danger:  'bg-danger/15 text-danger border-danger/40',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex flex-col items-center gap-1.5 px-2 py-3 rounded-sm border transition-colors
        ${active ? activeClass : 'border-transparent text-text-muted hover:text-text hover:bg-bg-hover'}
      `}
    >
      <Icon size={16} />
      <span className="text-2xs uppercase tracking-[0.1em]">{label}</span>
    </button>
  );
}

// Suppress unused imports
void Input;
